import { useEffect, useState } from "react";
import { COLORS, Wordmark } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { useIsDesktop } from "@/lib/media";
import Footer from "@/components/Footer";
import LoginContactFooter from "@/components/LoginContactFooter";
import { apiPost } from "@/lib/api";

import { isPhoneApp } from "@/lib/phoneApp";

export default function Auth() {
  const { login, register, formatApiError } = useAuth();
  const isDesktop = useIsDesktop();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm: "",
    phone: "", whatsapp: "", hebrew_name: "", family_name: "", address: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get("verify_token");
    const invitedEmail = params.get("email");
    if (invitedEmail) { setMode("register"); setForm((f) => ({ ...f, email: invitedEmail })); }
    if (verifyToken) {
      apiPost("/auth/verify-email", { token: verifyToken })
        .then((r) => { setNotice(r.message || "Email verified. Please sign in."); window.history.replaceState({}, "", window.location.pathname); })
        .catch((e) => setError(e?.response?.data?.detail || "Verification link could not be used."));
    }
  }, []);

  const submit = async () => {
    setLoading(true); setError("");
    try {
      if (mode === "login") {
        if (!form.email || !form.password) throw new Error("Email and password are required");
        await login(form.email, form.password);
      } else {
        if (!form.first_name || !form.last_name || !form.email) throw new Error("Name and email are required");
        if (isPhoneApp && !form.phone.trim()) throw new Error("Cell number is required");
        if (form.password.length < 8) throw new Error("Password must be at least 8 characters");
        if (form.password !== form.confirm) throw new Error("Passwords do not match");
        await register({
          first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password,
          phone: form.phone || null, whatsapp: form.whatsapp || form.phone || null, hebrew_name: form.hebrew_name || null, family_name: form.family_name || null, address: form.address || null,
        });
      }
    } catch (e) {
      setError(e?.response ? formatApiError(e) : e.message);
    } finally { setLoading(false); }
  };

  const Form = (
    <div style={{ width: "100%", maxWidth: 420, margin: "0 auto" }}>
      <div style={{ display: "flex", background: COLORS.lilac, borderRadius: 14, padding: 4, marginBottom: 20 }}>
        {[["login", "Sign In"], ["register", "Register"]].map(([k, label]) => (
          <button key={k} data-testid={`auth-tab-${k}`} onClick={() => { setMode(k); setError(""); }} className="no-tap-highlight"
            style={{ flex: 1, padding: "10px 0", borderRadius: 12, border: "none", cursor: "pointer", fontWeight: 800, fontFamily: "'Nunito', sans-serif",
              color: mode === k ? "#fff" : COLORS.purple,
              background: mode === k ? `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.plum})` : "transparent" }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {mode === "register" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input data-testid="auth-first-name" className="ls-input" placeholder="First name" value={form.first_name} onChange={set("first_name")} />
              <input data-testid="auth-last-name" className="ls-input" placeholder="Last name" value={form.last_name} onChange={set("last_name")} />
            </div>
            {!isPhoneApp && <input data-testid="auth-hebrew-name" className="ls-input" placeholder="Hebrew name (optional)" value={form.hebrew_name} onChange={set("hebrew_name")} />}
            {!isPhoneApp && <input data-testid="auth-family-name" className="ls-input" placeholder="Family name (optional)" value={form.family_name} onChange={set("family_name")} />}
            <input data-testid="auth-phone" className="ls-input" placeholder="Cell number" value={form.phone} onChange={set("phone")} />
            {!isPhoneApp && <input data-testid="auth-whatsapp" className="ls-input" placeholder="WhatsApp number (if different)" value={form.whatsapp} onChange={set("whatsapp")} />}
            {!isPhoneApp && <input data-testid="auth-address" className="ls-input" placeholder="Residential address (optional)" value={form.address} onChange={set("address")} />}
          </>
        )}
        <input data-testid="auth-email" className="ls-input" placeholder="Email" type="email" autoComplete="email" value={form.email} onChange={set("email")} />
        <input data-testid="auth-password" className="ls-input" placeholder="Password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} value={form.password} onChange={set("password")} />
        {mode === "register" && (
          <input data-testid="auth-confirm" className="ls-input" placeholder="Confirm password" type="password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} />
        )}
        {isPhoneApp && mode === "login" && <p>Information in the LinkShul app is confidential. Personal information is used only for Shul activities and is kept within LinkShul.</p>}
        {notice && <div style={{ color: "#26713d", fontSize: 13, fontWeight: 800, textAlign: "center", padding: "8px", background: "#edf9f0", borderRadius: 10 }}>{notice}</div>}
        {error && <div data-testid="auth-error" style={{ color: "#a02040", fontSize: 13, fontWeight: 700, textAlign: "center", padding: "4px 8px" }}>{error}</div>}
        <button data-testid="auth-submit" className="ls-primary-btn" onClick={submit} disabled={loading} style={{ marginTop: 8 }}>
          {loading ? "Please wait…" : mode === "login" ? "Sign In" : isPhoneApp ? "Submit for approval" : "Create Account"}
        </button>
        {mode === "register" && (
          <div style={{ marginTop: 8, padding: "10px 14px", background: COLORS.lilac, borderRadius: 12, fontSize: 12.5, color: COLORS.ink, fontWeight: 600, lineHeight: 1.5 }}>
            {isPhoneApp && "Access to the LinkShul app is subject to approval. "}After registering, your account will be reviewed by an administrator. Once approved, LinkShul will email you a welcome message and email-verification link.
          </div>
        )}
      </div>
    </div>
  );

  if (!isDesktop) {
    return (
      <div className="ls-shell" data-testid="auth-screen">
        <div style={{ background: `linear-gradient(160deg, ${COLORS.purple} 0%, ${COLORS.plum} 100%)`, padding: "50px 24px 30px", borderRadius: "0 0 32px 32px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ width: 130, borderRadius: 16, boxShadow: "0 12px 30px rgba(0,0,0,0.35)" }} />
          <Wordmark scale={1} light />
          <div style={{ color: "rgba(255,255,255,0.75)", fontWeight: 700, fontSize: 12, textAlign: "center" }}>
            {isPhoneApp ? "LinkShul" : "LinkShul"}
          </div>
        </div>
        <div className="ls-scroll" style={{ padding: "20px 22px 30px", height: "calc(100% - 240px)" }}>{Form}<LoginContactFooter /></div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div data-testid="auth-screen" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: COLORS.cream }}>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1.1fr 1fr", minHeight: "80vh" }}>
        <div style={{ background: `linear-gradient(160deg, ${COLORS.purple} 0%, ${COLORS.plum} 100%)`, color: "#fff", padding: "80px 60px", display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
          <div style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(201,162,75,0.14)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32, position: "relative", zIndex: 1 }}>
            <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ width: 120, borderRadius: 18, boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }} />
            <div>
              <div className="font-heading" style={{ fontWeight: 800, fontSize: 46, letterSpacing: "-1px", lineHeight: 1 }}>
                Link<span style={{ color: COLORS.goldLight }}>shul</span>
              </div>
              <div style={{ direction: "rtl", color: "rgba(255,255,255,0.7)", fontSize: 15, fontWeight: 700, marginTop: 6 }}>
                בית כנסת לינקספילד סנדרווד
              </div>
            </div>
          </div>
          <h1 className="font-heading" style={{ fontWeight: 800, fontSize: 40, lineHeight: 1.15, marginBottom: 16, position: "relative", zIndex: 1 }}>
            {isPhoneApp ? "LinkShul" : "LinkShul"}
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(255,255,255,0.85)", maxWidth: 520, fontWeight: 500, position: "relative", zIndex: 1 }}>
            A warm, united and innovative Orthodox community in the eastern suburbs of Johannesburg. Sign in with your member account or register to join the family.
          </p>
          <div style={{ marginTop: 40, display: "flex", gap: 24, flexWrap: "wrap", position: "relative", zIndex: 1 }}>
            {[["Daily", "minyanim"], ["Weekly", "learning shiurim"], ["All-ages", "community events"]].map(([a, b]) => (
              <div key={a}>
                <div className="font-heading" style={{ fontSize: 28, fontWeight: 800, color: COLORS.goldLight }}>{a}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{b}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 40px" }}>
          <div style={{ width: "100%", maxWidth: 460 }}>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 30, color: COLORS.ink, marginBottom: 6 }}>
              {mode === "login" ? "Welcome back" : "Create your account"}
            </div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 14, marginBottom: 28 }}>
              {mode === "login" ? "Sign in to access the LinkShul community portal." : "Registrations are reviewed by an administrator."}
            </div>
            {Form}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
